import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Trash2, Minus, Plus, ShoppingCart, ChevronRight, Mail, AlertTriangle, Clock, X } from "lucide-react";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { ShowcaseHeroBackground } from "@/components/ShowcaseHeroBackground";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CartPage() {
  const { items, removeItem, updateQuantity, clearCart, totalPrice } = useCart();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  // Re-check auth on cart page load to pick up email verification done in another browser/tab
  useEffect(() => { if (user) checkAuth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [cancellingWaitlist, setCancellingWaitlist] = useState(null);
  const [waitlistError, setWaitlistError] = useState("");
  const [stockMap, setStockMap] = useState({});

  // Shipping address state
  const [shipping, setShipping] = useState({
    full_name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US", phone: "",
  });
  const [shippingErrors, setShippingErrors] = useState({});

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [couponApplying, setCouponApplying] = useState(false);
  const [coupon, setCoupon] = useState(null); // { code, discount, free_shipping, type, value }
  const [couponError, setCouponError] = useState("");

  // Fetch shipping estimate when cart items change
  useEffect(() => {
    if (items.length === 0) {
      setShippingEstimate(null);
      return;
    }
    const fetchShipping = async () => {
      setShippingLoading(true);
      try {
        const { data } = await axios.post(`${API}/api/shipping/estimate`, items.map(i => ({ product_id: i.product_id, quantity: i.quantity })));
        setShippingEstimate(data);
      } catch {
        setShippingEstimate(null);
      } finally {
        setShippingLoading(false);
      }
    };
    fetchShipping();
  }, [items]);

  // Fetch stock levels for cart items
  const [stockLoaded, setStockLoaded] = useState(false);
  useEffect(() => {
    if (items.length === 0) return;
    setStockLoaded(false);
    const fetchStock = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        const map = {};
        data.forEach(p => { map[p.id] = p.stock; });
        setStockMap(map);
      } catch { /* ignore */ }
      setStockLoaded(true);
    };
    fetchStock();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user's waitlist entries
  const fetchWaitlist = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/api/waitlist/my`);
      setWaitlistEntries(data);
    } catch {
      // ignore
    }
  }, [user]);

  useEffect(() => { fetchWaitlist(); }, [fetchWaitlist]);

  const handleCancelWaitlist = async (entryId) => {
    setCancellingWaitlist(entryId);
    setWaitlistError("");
    try {
      await axios.delete(`${API}/api/waitlist/${entryId}`);
      setWaitlistEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (e) {
      const detail = e.response?.data?.detail;
      setWaitlistError(typeof detail === "string" ? detail : "Failed to cancel waitlist entry. Please try again.");
    } finally {
      setCancellingWaitlist(null);
    }
  };

  const shippingTotal = coupon?.free_shipping ? 0 : (shippingEstimate?.shipping_total || 0);
  const discount = coupon?.discount || 0;
  const orderTotal = Math.max(0, totalPrice + shippingTotal - discount);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponApplying(true);
    setCouponError("");
    try {
      const { data } = await axios.post(`${API}/api/coupons/validate`, {
        code,
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_color: i.selected_color || null })),
      });
      if (data.valid) {
        setCoupon(data);
        setCouponInput("");
      } else {
        setCouponError(data.reason || "Invalid code");
      }
    } catch (e) {
      setCouponError("Could not validate code. Try again.");
    } finally {
      setCouponApplying(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponError("");
  };

  // Re-validate coupon if cart contents change (cart total may now be below min_order_value etc.)
  useEffect(() => {
    if (!coupon || items.length === 0) return;
    const revalidate = async () => {
      try {
        const { data } = await axios.post(`${API}/api/coupons/validate`, {
          code: coupon.code,
          items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_color: i.selected_color || null })),
        });
        if (data.valid) {
          setCoupon(data);
        } else {
          setCoupon(null);
          setCouponError(`${coupon.code} no longer valid: ${data.reason}`);
        }
      } catch {
        // keep coupon, will re-check at checkout
      }
    };
    revalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map(i => `${i.product_id}-${i.quantity}-${i.selected_color || ""}`).join("|")]);

  const validateShipping = () => {
    const errs = {};
    if (!shipping.full_name.trim()) errs.full_name = "Required";
    if (!shipping.address_line1.trim()) errs.address_line1 = "Required";
    if (!shipping.city.trim()) errs.city = "Required";
    if (!shipping.state.trim()) errs.state = "Required";
    if (!shipping.zip_code.trim()) errs.zip_code = "Required";
    setShippingErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleResendVerification = async () => {
    setResending(true);
    setResendMsg("");
    try {
      const { data } = await axios.post(`${API}/api/auth/resend-verification`, {});
      setResendMsg(data.message || "Verification email sent!");
    } catch {
      setResendMsg("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate("/login?redirect=/cart");
      return;
    }
    if (!user.email_verified) {
      setError("Please verify your email before checkout. Check your inbox for the verification link.");
      return;
    }
    if (!validateShipping()) {
      setError("Please fill in all required shipping fields.");
      return;
    }
    setCheckingOut(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/api/checkout/create-session`,
        {
          items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_color: i.selected_color || null })),
          origin_url: window.location.origin,
          shipping_address: shipping,
          coupon_code: coupon?.code || null,
        },
        { withCredentials: false }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to create checkout session");
    } finally {
      setCheckingOut(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
        {/* Top hero band — subtle showcase backdrop even when cart is empty. */}
        <div className="relative overflow-hidden border-b border-[#1A1A1A]">
          <ShowcaseHeroBackground intervalMs={9000} />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Cart</p>
            <h1
              className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Your Cart
            </h1>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex flex-col items-center justify-center gap-6 py-16">
            <ShoppingCart className="h-16 w-16 text-[#1A1A1A]" strokeWidth={1} />
            <h1 className="text-2xl font-bold uppercase tracking-tight text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Your cart is empty
            </h1>
            <Link to="/products">
              <Button
                data-testid="continue-shopping"
                className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-6"
              >
                Continue Shopping
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Waitlist Section - shown even with empty cart */}
          {user && waitlistEntries.length > 0 && (
            <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 mt-8" data-testid="waitlist-section-empty">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                <h3 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Your Waitlist</h3>
              </div>
              {waitlistError && (
                <div className="mb-3 p-3 border border-red-500/30 bg-red-500/10 flex items-center justify-between" data-testid="waitlist-error-empty">
                  <span className="text-red-400 text-xs font-mono">{waitlistError}</span>
                  <button onClick={() => setWaitlistError("")} className="text-red-400 hover:text-red-300 ml-2">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="space-y-3">
                {waitlistEntries.map((entry) => (
                  <div key={entry.id} data-testid={`waitlist-entry-${entry.id}`} className="flex items-center justify-between p-3 border border-[#1A1A1A] bg-[#050505]">
                    <div className="min-w-0 flex-1">
                      <Link to={`/products/${entry.product_id}`} className="text-[#F2F2F2] text-sm font-bold hover:text-[#39FF14] transition-colors truncate block">
                        {entry.product_name}
                      </Link>
                      <p className="text-[#A1A1A1] text-xs font-mono mt-1">
                        On the waitlist — we will reach out as more become available
                      </p>
                    </div>
                    <button
                      onClick={() => handleCancelWaitlist(entry.id)}
                      disabled={cancellingWaitlist === entry.id}
                      data-testid={`cancel-waitlist-${entry.id}`}
                      className="ml-3 px-3 py-1.5 border border-[#1A1A1A] hover:border-red-500 text-[#A1A1A1] hover:text-red-400 hover:bg-red-500/10 transition-colors font-mono text-[10px] uppercase tracking-wider disabled:opacity-50"
                    >
                      {cancellingWaitlist === entry.id ? "..." : "Cancel"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      {/* Top hero band — greyed-out showcase photo backdrop behind the heading. */}
      <div className="relative overflow-hidden border-b border-[#1A1A1A]">
        <ShowcaseHeroBackground intervalMs={9000} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Cart</p>
          <h1
            data-testid="cart-heading"
            className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Your Cart
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items + Shipping */}
          <div className="lg:col-span-2 space-y-6">
            {/* Items */}
            <div className="space-y-[1px] bg-[#1A1A1A]">
              {items.map((item) => (
                <div
                  key={item.product_id}
                  data-testid={`cart-item-${item.product_id}`}
                  className="bg-[#0D0D0D] p-6 flex gap-4"
                >
                  <div className="w-20 h-20 flex-shrink-0 border border-[#1A1A1A] overflow-hidden">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#0D0D0D]">
                        <span className="font-mono text-[#1A1A1A] text-2xl">X</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.product_id}`}>
                      <h3 className="text-[#F2F2F2] font-bold uppercase tracking-tight text-sm hover:text-[#39FF14] transition-colors" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {item.name}
                      </h3>
                    </Link>
                    <p className="font-mono text-sm text-[#39FF14] mt-1">${item.price.toFixed(2)}</p>
                    {item.selected_color && <p className="font-mono text-xs text-[#A1A1A1] mt-0.5">Color: <span className="text-[#F2F2F2]">{item.selected_color}</span>{item.price > 0 && ["consoles","bundles"].some(c => item.name?.toLowerCase().includes(c) || false) ? "" : ""}</p>}
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-[#1A1A1A]">
                        <button data-testid={`cart-decrease-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="px-2 py-1 text-[#A1A1A1] hover:text-[#39FF14]">
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 py-1 font-mono text-xs text-[#F2F2F2] border-l border-r border-[#1A1A1A]">{item.quantity}</span>
                        <button
                          data-testid={`cart-increase-${item.product_id}`}
                          onClick={() => { const max = stockMap[item.product_id]; if (max !== undefined && item.quantity < max) updateQuantity(item.product_id, item.quantity + 1); }}
                          disabled={!stockLoaded || stockMap[item.product_id] === undefined || item.quantity >= stockMap[item.product_id]}
                          className="px-2 py-1 text-[#A1A1A1] hover:text-[#39FF14] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-[#A1A1A1]"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="text-[#A1A1A1] hover:text-red-500 transition-colors">
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-sm font-bold text-[#F2F2F2]">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Email Verification Warning */}
            {user && !user.email_verified && (
              <div className="bg-[#0D0D0D] border border-yellow-600/30 p-5" data-testid="email-verification-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-yellow-400 text-sm font-bold mb-1">Email Not Verified</p>
                    <p className="text-[#A1A1A1] text-sm mb-3">
                      You need to verify your email ({user.email}) before you can checkout.
                    </p>
                    <button
                      onClick={handleResendVerification}
                      disabled={resending}
                      data-testid="resend-verification-btn"
                      className="inline-flex items-center gap-2 text-[#39FF14] hover:underline font-mono text-xs uppercase tracking-wider disabled:opacity-50"
                    >
                      <Mail className="h-3 w-3" />
                      {resending ? "Sending..." : "Resend Verification Email"}
                    </button>
                    {resendMsg && <p className="text-[#A1A1A1] text-xs mt-2">{resendMsg}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Waitlist Section */}
            {user && waitlistEntries.length > 0 && (
              <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-6" data-testid="waitlist-section">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                  <h3 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Your Waitlist</h3>
                </div>
                {waitlistError && (
                  <div className="mb-3 p-3 border border-red-500/30 bg-red-500/10 flex items-center justify-between" data-testid="waitlist-error">
                    <span className="text-red-400 text-xs font-mono">{waitlistError}</span>
                    <button onClick={() => setWaitlistError("")} className="text-red-400 hover:text-red-300 ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <div className="space-y-3">
                  {waitlistEntries.map((entry) => (
                    <div key={entry.id} data-testid={`waitlist-entry-${entry.id}`} className="flex items-center justify-between p-3 border border-[#1A1A1A] bg-[#050505]">
                      <div className="min-w-0 flex-1">
                        <Link to={`/products/${entry.product_id}`} className="text-[#F2F2F2] text-sm font-bold hover:text-[#39FF14] transition-colors truncate block">
                          {entry.product_name}
                        </Link>
                        <p className="text-[#A1A1A1] text-xs font-mono mt-1">
                          On the waitlist — we will reach out as more become available
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelWaitlist(entry.id)}
                        disabled={cancellingWaitlist === entry.id}
                        data-testid={`cancel-waitlist-${entry.id}`}
                        className="ml-3 px-3 py-1.5 border border-[#1A1A1A] hover:border-red-500 text-[#A1A1A1] hover:text-red-400 hover:bg-red-500/10 transition-colors font-mono text-[10px] uppercase tracking-wider disabled:opacity-50"
                      >
                        {cancellingWaitlist === entry.id ? "..." : "Cancel"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipping Address */}
            <form autoComplete="on" onSubmit={(e) => e.preventDefault()} className="bg-[#0D0D0D] border border-[#1A1A1A] p-6" data-testid="shipping-form">
              <h3 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-5">Shipping Address</h3>
              {user && (
                <div className="mb-4 p-3 border border-[#1A1A1A] bg-[#050505]">
                  <p className="font-mono text-xs text-[#A1A1A1] uppercase tracking-wider mb-1">Email</p>
                  <p className="text-[#F2F2F2] text-sm flex items-center gap-2" data-testid="checkout-email">
                    {user.email}
                    {user.email_verified && <span className="text-[#39FF14] font-mono text-[10px]">VERIFIED</span>}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: "full_name", label: "Full Name", span2: true, auto: "name" },
                  { id: "address_line1", label: "Address Line 1", span2: true, auto: "address-line1" },
                  { id: "address_line2", label: "Address Line 2 (Optional)", span2: true, auto: "address-line2" },
                  { id: "city", label: "City", auto: "address-level2" },
                  { id: "state", label: "State", auto: "address-level1" },
                  { id: "zip_code", label: "ZIP Code", auto: "postal-code" },
                  { id: "country", label: "Country", auto: "country" },
                  { id: "phone", label: "Phone (for shipping issues)", span2: true, auto: "tel" },
                ].map(({ id, label, span2, auto }) => (
                  <div key={id} className={`space-y-1 ${span2 ? "sm:col-span-2" : ""}`}>
                    <Label htmlFor={id} className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">{label}</Label>
                    <input
                      type="text"
                      id={id} name={id} autoComplete={auto}
                      data-testid={`shipping-${id}`}
                      value={shipping[id]}
                      onChange={(e) => setShipping(s => ({ ...s, [id]: e.target.value }))}
                      className={`flex h-9 w-full bg-[#0D0D0D] border ${shippingErrors[id] ? "border-[#ef4444]" : "border-[#1A1A1A]"} text-[#F2F2F2] px-3 py-1 text-sm focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14]`}
                    />
                  </div>
                ))}
              </div>
            </form>
          </div>

          {/* Summary */}
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 h-fit sticky top-24">
            <h3 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-6">Order Summary</h3>

            <div className="space-y-3 mb-6">
              {items.map(item => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-[#A1A1A1] truncate mr-2">{item.name} x{item.quantity}</span>
                  <span className="font-mono text-[#F2F2F2]">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-[#1A1A1A] pt-4 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1A1]">Subtotal</span>
                <span className="font-mono text-[#F2F2F2]">${totalPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#A1A1A1]">
                  Shipping
                  <span className="block font-mono text-[10px] text-[#A1A1A1]/60">USPS Ground Advantage</span>
                </span>
                <span className="font-mono text-[#F2F2F2]">
                  {shippingLoading ? "..." : coupon?.free_shipping ? <span className="text-[#39FF14]">FREE</span> : `$${shippingTotal.toFixed(2)}`}
                </span>
              </div>

              {/* Coupon Code */}
              {coupon ? (
                <div className="flex justify-between items-center text-sm py-2 border border-[#39FF14]/30 bg-[#39FF14]/5 px-3" data-testid="coupon-applied">
                  <span className="text-[#39FF14] font-mono text-xs">
                    <span className="font-bold">{coupon.code}</span>
                    {coupon.type === "percent" && <span className="text-[#A1A1A1] ml-2">({coupon.value}% off)</span>}
                    {coupon.type === "amount" && <span className="text-[#A1A1A1] ml-2">(${coupon.value} off)</span>}
                    {coupon.type === "free_shipping" && <span className="text-[#A1A1A1] ml-2">(free shipping)</span>}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[#39FF14]">{coupon.discount > 0 ? `-$${coupon.discount.toFixed(2)}` : ""}</span>
                    <button onClick={removeCoupon} data-testid="coupon-remove" className="text-[#A1A1A1] hover:text-red-400" title="Remove">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      data-testid="coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                      placeholder="Discount code"
                      className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono text-xs h-9 uppercase"
                    />
                    <Button
                      type="button"
                      onClick={applyCoupon}
                      disabled={couponApplying || !couponInput.trim()}
                      data-testid="coupon-apply"
                      variant="outline"
                      className="bg-transparent border-[#1A1A1A] text-[#39FF14] hover:bg-[#39FF14]/10 hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider h-9 px-4 disabled:opacity-50"
                    >
                      {couponApplying ? "..." : "Apply"}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 text-xs font-mono" data-testid="coupon-error">{couponError}</p>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-3 border-t border-[#1A1A1A]">
                <span className="text-[#F2F2F2] font-bold uppercase text-sm">Total</span>
                <span className="font-mono text-xl font-bold text-[#39FF14]" data-testid="cart-total">
                  ${orderTotal.toFixed(2)}
                </span>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 border border-red-500/30 bg-red-500/10 text-red-400 text-sm" data-testid="checkout-error">
                {error}
              </div>
            )}

            <Button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={checkingOut || (user && !user.email_verified)}
              className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6 transition-colors duration-200 disabled:opacity-50"
            >
              {checkingOut ? "Processing..." : !user ? "Sign In to Checkout" : !user.email_verified ? "Verify Email First" : "Checkout with Stripe"}
            </Button>

            <button
              data-testid="clear-cart-btn"
              onClick={clearCart}
              className="w-full mt-3 text-center font-mono text-xs text-[#A1A1A1] hover:text-red-400 uppercase tracking-wider transition-colors"
            >
              Clear Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
